import { useEffect, useState } from "react";
import { X, Share, Plus, Bell, Smartphone, Monitor } from "lucide-react";

/**
 * Nudges users to install LIM Flow as a PWA so alarms & chat pings can
 * ring even when the browser tab is closed.
 *
 * On Chrome / Edge / Android we listen for `beforeinstallprompt` and show a
 * one-click "Install app" button. On iOS Safari and macOS Safari we fall back
 * to a plain instruction card (there's no programmatic install on Apple).
 *
 * Hidden entirely when the app is already running standalone, or the user
 * has previously dismissed the prompt (localStorage flag). No prompt while
 * the tab is blurred so it never steals a click.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);   // beforeinstallprompt event
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("lim-install-dismissed") === "1");
  const [platform, setPlatform] = useState("");     // 'ios' | 'safari-mac' | 'other'
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Already running as an installed PWA? Hide.
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) { setVisible(false); return; }
    if (dismissed) { setVisible(false); return; }

    // Detect platform (rough — enough for the copy switch).
    const ua = navigator.userAgent || "";
    const isIOS = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
    const isMacSafari = /Macintosh/.test(ua) && /Safari\//.test(ua) && !/Chrome|Chromium|CriOS/.test(ua);
    if (isIOS) setPlatform("ios");
    else if (isMacSafari) setPlatform("safari-mac");
    else setPlatform("other");

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // On iOS / macOS Safari the event never fires — reveal the manual card
    // after 3s so it doesn't compete with initial page load.
    if (isIOS || isMacSafari) {
      const t = setTimeout(() => setVisible(true), 3000);
      return () => { window.removeEventListener("beforeinstallprompt", handler); clearTimeout(t); };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [dismissed]);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (_) { /* silent */ }
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem("lim-install-dismissed", "1");
    setDismissed(true);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[9998]"
    >
      <div className="rounded-xl border border-[#E11D2E]/40 bg-gradient-to-br from-surface to-surface shadow-2xl shadow-[#E11D2E]/20 overflow-hidden">
        <div className="flex items-start gap-3 p-4">
          <div className="h-10 w-10 rounded-lg bg-[#E11D2E]/15 border border-[#E11D2E]/40 flex items-center justify-center shrink-0">
            <Bell size={18} className="text-red-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[0.7rem] uppercase tracking-widest text-red-300 font-mono">Install LIM Flow</div>
            <div className="text-sm text-white font-medium leading-tight mt-0.5">
              Get alarms & pings even when the tab is closed
            </div>
            {platform === "ios" && (
              <div className="text-xs text-white/70 mt-2 leading-relaxed">
                <div className="flex items-center gap-1.5"><Smartphone size={11} /> On iPhone / iPad:</div>
                <ol className="list-decimal list-inside mt-1 space-y-0.5 text-white/60">
                  <li>Tap the <Share size={10} className="inline mx-0.5" /> Share button in Safari</li>
                  <li>Choose <b>Add to Home Screen</b></li>
                  <li>Open LIM Flow from the new icon and enable notifications</li>
                </ol>
              </div>
            )}
            {platform === "safari-mac" && (
              <div className="text-xs text-white/70 mt-2 leading-relaxed">
                <div className="flex items-center gap-1.5"><Monitor size={11} /> On Mac (Safari 16+):</div>
                <ol className="list-decimal list-inside mt-1 space-y-0.5 text-white/60">
                  <li>Click <b>File → Add to Dock…</b> in the top menu</li>
                  <li>Confirm the name and click Add</li>
                  <li>Launch LIM Flow from the Dock icon</li>
                </ol>
              </div>
            )}
            {platform === "other" && (
              <div className="text-xs text-white/60 mt-1.5">
                One click to install as an app so notifications work in the background.
              </div>
            )}
            <div className="flex items-center gap-2 mt-3">
              {deferred && (
                <button
                  onClick={install}
                  data-testid="install-prompt-install-btn"
                  className="text-xs px-3 py-1.5 rounded bg-[#E11D2E] hover:bg-[#F03041] text-white font-medium inline-flex items-center gap-1.5"
                >
                  <Plus size={12} /> Install app
                </button>
              )}
              <button
                onClick={dismiss}
                data-testid="install-prompt-dismiss-btn"
                className="text-xs px-3 py-1.5 rounded border border-white/15 text-white/60 hover:text-white hover:border-white/30"
              >
                {deferred ? "Not now" : "Got it"}
              </button>
            </div>
          </div>
          <button
            onClick={dismiss}
            data-testid="install-prompt-close-btn"
            className="text-white/40 hover:text-white/80 p-1 -m-1"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
